// ═══════════════════════════════════════════════════════════════
// LinkedExtras.tsx – kapcsolt szervek kiegészítő felületei
//   • LinkedRelationFields – kapcsolódás (melyik szervhez, milyen kapcsolat, alá / mellé)
//   • LinkedExtraBlock     – „Közzétételi kiegészítő adatok” (alapból csukva, szervezeti szinten mentett állapot)
//   • RelationTypesEditor  – a kapcsolattípusok szerkeszthető listája
//   • LinkedMapEditor      – a kapcsolati térkép szerkesztője
// ═══════════════════════════════════════════════════════════════
import React, { useState } from 'react';
import type { LinkedEntity, LinkedEntityType, LinkedSettings } from '../../types';
import { ApiClient } from '../../api/client';
import { EXTRA_GROUPS, extraFillState, extraGroupsFor, entityDescendants, relationLabel, ENTITY_TYPE_LABEL } from './linkedModel';
import { LinkedMapView } from './PersonnelBlock';
import { PhoneInput } from '../common/PhoneInput';
import { ChevronDown, ChevronRight, Info, Plus, Save, Settings2, Trash2, RefreshCw, RectangleVertical, RectangleHorizontal, Copy, Check, Network, X } from 'lucide-react';

const inputCls = 'w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white';
const labelCls = 'block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1';

type Edit = Partial<LinkedEntity>;

export const LinkedRelationFields: React.FC<{
  item: Edit; setItem: (e: Edit) => void; entities: LinkedEntity[]; settings: LinkedSettings; disabled?: boolean;
  onEditTypes?: () => void;
}> = ({ item, setItem, entities, settings, disabled, onEditTypes }) => {
  const exclude = item.id ? entityDescendants(entities, item.id) : new Set<number>();
  const options = entities.filter((e) => !exclude.has(e.id)).sort((a, b) => a.name.localeCompare(b.name, 'hu'));
  const known = new Set(settings.relation_types.map((r) => r.key));
  return (
    <fieldset disabled={disabled} className="rounded-xl border border-slate-200 dark:border-slate-800 p-3 space-y-3 disabled:opacity-50">
      <legend className="px-1 text-xs font-bold text-slate-800 dark:text-slate-200">Kapcsolódás (kapcsolati térkép)</legend>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={labelCls} htmlFor="le-parent">Kihez kapcsolódik</label>
          <select id="le-parent" className={inputCls} value={item.parent_entity_id || ''}
            onChange={(e) => setItem({ ...item, parent_entity_id: e.target.value ? Number(e.target.value) : null })}>
            <option value="">— Közvetlenül az anyaszervezethez —</option>
            {options.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300" htmlFor="le-rel">Kapcsolat típusa</label>
            {onEditTypes && (
              <button type="button" onClick={onEditTypes} className="inline-flex items-center gap-1 text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer">
                <Settings2 className="w-3 h-3" /> Típusok szerkesztése
              </button>
            )}
          </div>
          <select id="le-rel" className={inputCls} value={item.relation_type || ''}
            onChange={(e) => setItem({ ...item, relation_type: e.target.value || null })}>
            <option value="">— nincs megadva —</option>
            {settings.relation_types.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            {item.relation_type && !known.has(item.relation_type) && <option value={item.relation_type}>{item.relation_type} (törölt típus)</option>}
          </select>
          <p className="mt-1 text-[10px] text-slate-500">A fölérendelt (anyaszervezet vagy a kiválasztott szerv) szerepe, pl. „Fenntartó”.</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 text-xs">
        {([['below', 'Alá', 'alárendelt kapcsolat'], ['beside', 'Mellé', 'oldalági kapcsolat']] as const).map(([v, t, d]) => (
          <label key={v} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border cursor-pointer ${(item.placement || 'below') === v ? 'border-indigo-500 bg-indigo-50/60 dark:bg-indigo-950/30' : 'border-slate-200 dark:border-slate-700'}`}>
            <input type="radio" name="le-placement" checked={(item.placement || 'below') === v} onChange={() => setItem({ ...item, placement: v })} />
            <b>{t}</b> <span className="text-slate-500">({d})</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
};

export const LinkedExtraBlock: React.FC<{
  item: Edit; setItem: (e: Edit) => void; open: boolean; onToggle: () => void; disabled?: boolean;
}> = ({ item, setItem, open, onToggle, disabled }) => {
  const [showAll, setShowAll] = useState(false);
  const fill = extraFillState(item);
  const groups = extraGroupsFor(item.entity_type as LinkedEntityType, showAll);
  const seen = new Set<string>();
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800">
      <button type="button" onClick={onToggle} aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 rounded-xl">
        <span className="flex items-center gap-1.5 text-xs font-bold text-slate-800 dark:text-slate-200">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          Közzétételi kiegészítő adatok
          <span className="font-normal text-slate-500">(Infotv. 1. melléklet I. rész)</span>
        </span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${fill.filled === fill.total ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800' : 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800'}`}>
          {fill.filled}/{fill.total} kitöltve
        </span>
      </button>
      {open && (
        <fieldset disabled={disabled} className="px-3 pb-3 space-y-3 disabled:opacity-50">
          {disabled && <p className="text-[11px] text-amber-700 dark:text-amber-300">A mezők az adatbázis frissítése (v61) után menthetők.</p>}
          {groups.map((g) => (
            <div key={g.id} className="space-y-2">
              <div className="text-[11px] font-bold text-slate-600 dark:text-slate-400">{g.title} <span className="font-normal">– {g.law}</span></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {g.fields.filter((f) => { const dup = seen.has(f.key as string); seen.add(f.key as string); return !dup; }).map((f) => (
                  <div key={f.key as string} className={f.multiline ? 'sm:col-span-2' : ''}>
                    <label className={labelCls} htmlFor={`le-x-${String(f.key)}`}>{f.label}</label>
                    {f.kind === 'phone' ? (
                      <PhoneInput id={`le-x-${String(f.key)}`} compact className={inputCls} placeholder={f.placeholder}
                        value={(item[f.key] as string) || ''} onChange={(v) => setItem({ ...item, [f.key]: v })} />
                    ) : f.multiline ? (
                      <textarea id={`le-x-${String(f.key)}`} rows={2} className={`${inputCls} resize-y`} placeholder={f.placeholder}
                        value={(item[f.key] as string) || ''} onChange={(e) => setItem({ ...item, [f.key]: e.target.value })} />
                    ) : (
                      <input id={`le-x-${String(f.key)}`} className={inputCls} placeholder={f.placeholder}
                        type={f.kind === 'url' ? 'url' : 'text'} inputMode={f.kind === 'percent' ? 'decimal' : undefined}
                        value={item[f.key] === null || item[f.key] === undefined ? '' : String(item[f.key])}
                        onChange={(e) => setItem({ ...item, [f.key]: e.target.value })} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <button type="button" onClick={() => setShowAll((v) => !v)} className="text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer">
            {showAll ? 'Csak a szervtípushoz tartozó mezők' : `Minden mező (${EXTRA_GROUPS.length} csoport)`}
          </button>
        </fieldset>
      )}
    </div>
  );
};

export const RelationTypesEditor: React.FC<{
  settings: LinkedSettings; entities: LinkedEntity[]; tenantId?: number; onClose: () => void; onSaved: (s: LinkedSettings) => void;
}> = ({ settings, entities, tenantId, onClose, onSaved }) => {
  const [rows, setRows] = useState(settings.relation_types.map((r) => ({ ...r })));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const usage = (key: string) => entities.filter((e) => e.relation_type === key).length;
  const save = async () => {
    setSaving(true); setError(null);
    const res = await ApiClient.saveLinkedSettings({ relation_types: rows.filter((r) => r.label.trim()) }, tenantId);
    setSaving(false);
    if (res.ok && res.data) onSaved(res.data);
    else setError(res.error || 'Hiba a mentéskor');
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60" role="dialog" aria-modal="true" aria-labelledby="rt-title">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-md shadow-2xl">
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <h3 id="rt-title" className="text-sm font-bold text-slate-900 dark:text-white">Kapcsolattípusok</h3>
          <button type="button" onClick={onClose} aria-label="Bezárás" className="text-slate-400 hover:text-slate-700 cursor-pointer"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-2 max-h-[60vh] overflow-y-auto">
          <p className="text-[11px] text-slate-500">A lista a szervezet minden szervénél használható. Átnevezéskor a meglévő hozzárendelések megmaradnak.</p>
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              <input aria-label={`${i + 1}. típus megnevezése`} className={inputCls} value={r.label} maxLength={80}
                onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} placeholder="pl. Megbízó" />
              <span className="text-[10px] text-slate-400 w-14 shrink-0 text-right">{r.key && usage(r.key) ? `${usage(r.key)} szerv` : ''}</span>
              <button type="button" aria-label="Típus törlése" title={r.key && usage(r.key) ? 'Használatban van – a szerveknél „törölt típus”-ként megmarad' : 'Törlés'}
                onClick={() => setRows(rows.filter((_, j) => j !== i))} className="p-1.5 text-slate-400 hover:text-rose-600 cursor-pointer"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          <button type="button" onClick={() => setRows([...rows, { key: '', label: '' }])}
            className="inline-flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 font-semibold cursor-pointer"><Plus className="w-3.5 h-3.5" /> Új típus</button>
          {error && <p className="text-xs text-rose-600" role="alert">{error}</p>}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs rounded-lg text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer">Mégse</button>
          <button type="button" onClick={save} disabled={saving} className="px-3 py-1.5 text-xs rounded-lg bg-indigo-600 text-white font-semibold disabled:opacity-50 cursor-pointer inline-flex items-center gap-1">
            {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Mentés
          </button>
        </div>
      </div>
    </div>
  );
};

/** a kapcsolati térkép szerkesztője (Kapcsolt szervek → Kapcsolati térkép fül) */
export const LinkedMapEditor: React.FC<{
  entities: LinkedEntity[]; settings: LinkedSettings; tenantName: string; tenantId?: number; canEdit: boolean; mapReady: boolean;
  onChanged: () => Promise<void> | void; onSettings: (s: LinkedSettings) => void;
  onAdd: (parentId: number | null) => void; onEdit: (e: LinkedEntity) => void;
}> = ({ entities, settings, tenantName, tenantId, canEdit, mapReady, onChanged, onSettings, onAdd, onEdit }) => {
  const [draft, setDraft] = useState(settings.map);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const dirty = JSON.stringify(draft) !== JSON.stringify(settings.map);
  const byId = new Map(entities.map((e) => [e.id, e]));
  const parentOf = (e: LinkedEntity) => (e.parent_entity_id && byId.has(e.parent_entity_id) ? e.parent_entity_id : null);
  const siblings = (pid: number | null, placement: string) => entities
    .filter((x) => parentOf(x) === pid && (x.placement || 'below') === placement)
    .sort((a, b) => ((a.sort_order || 0) - (b.sort_order || 0)) || a.name.localeCompare(b.name, 'hu'));
  const nextOrder = (pid: number | null, placement: string) => siblings(pid, placement).reduce((m, x) => Math.max(m, x.sort_order || 0), 0) + 1;

  const run = async (ops: { id: number; patch: Partial<LinkedEntity> }[]) => {
    if (!ops.length) return;
    setBusy(true);
    try {
      for (const op of ops) {
        const r = await ApiClient.updateLinkedEntity(op.id, op.patch);
        if (!r.ok) { alert(r.error || 'Hiba a módosításkor'); break; }
      }
    } finally { await onChanged(); setBusy(false); }
  };
  const edit = canEdit && mapReady ? {
    onMove: (id: number, newParent: number | null) => {
      const e = byId.get(id);
      if (!e || parentOf(e) === newParent) return;
      run([{ id, patch: { parent_entity_id: newParent, sort_order: nextOrder(newParent, e.placement || 'below') } }]);
    },
    onTogglePlacement: (u: { id: number; placement?: string }) => {
      const e = byId.get(u.id); if (!e) return;
      const next = (e.placement || 'below') === 'beside' ? 'below' : 'beside';
      run([{ id: e.id, patch: { placement: next, sort_order: nextOrder(parentOf(e), next) } }]);
    },
    onReorder: (u: { id: number }, dir: -1 | 1) => {
      const e = byId.get(u.id); if (!e) return;
      const sib = siblings(parentOf(e), e.placement || 'below');
      const i = sib.findIndex((x) => x.id === e.id); const j = i + dir;
      if (i < 0 || j < 0 || j >= sib.length) return;
      const arr = [...sib]; [arr[i], arr[j]] = [arr[j], arr[i]];
      run(arr.map((x, k) => ({ id: x.id, patch: { sort_order: k + 1 }, cur: x.sort_order })).filter((o) => o.cur !== o.patch.sort_order).map(({ id, patch }) => ({ id, patch })));
    },
    onAddChild: (pid: number | null) => onAdd(pid),
    onEdit: (u: { id: number }) => { const e = byId.get(u.id); if (e) onEdit(e); },
  } : undefined;

  const saveMap = async () => {
    setBusy(true); setMsg(null);
    const r = await ApiClient.saveLinkedSettings({ map: draft }, tenantId);
    setBusy(false);
    if (r.ok && r.data) { onSettings(r.data); setMsg('Elmentve – a nyilvános oldalon is így jelenik meg.'); setTimeout(() => setMsg(null), 3500); }
    else setMsg(r.error || 'Hiba a mentéskor');
  };
  const copy = async (t: string) => { try { await navigator.clipboard.writeText(t); } catch { /* */ } setCopied(t); setTimeout(() => setCopied((c) => (c === t ? null : c)), 1800); };
  const types = [...new Set(entities.map((e) => e.entity_type))];
  const tokens = [
    { t: '{{KAPCSOLATI_TERKEP}}', l: 'Kapcsolati térkép' },
    { t: '{{KAPCSOLT_SZERVEK}}', l: 'Kapcsolt szervek – mind' },
    ...types.map((t) => ({ t: `{{KAPCSOLT_SZERVEK:${t}}}`, l: `Csak: ${ENTITY_TYPE_LABEL[t as LinkedEntityType] || t}` })),
    ...entities.map((e) => ({ t: `{{KAPCSOLT_SZERV:${e.id}}}`, l: `Adatlap – ${e.short_name || e.name}` })),
  ];

  return (
    <div className="space-y-4">
      <div className="p-3 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-wrap items-end gap-3">
        <div>
          <span className={labelCls}>Tájolás</span>
          <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200 dark:border-slate-700" role="group" aria-label="Tájolás">
            {([['vertical', 'Álló', RectangleVertical], ['horizontal', 'Fekvő', RectangleHorizontal]] as const).map(([v, l, Icon]) => (
              <button key={v} type="button" aria-pressed={draft.orientation === v} onClick={() => setDraft({ ...draft, orientation: v })}
                className={`px-2 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 cursor-pointer ${draft.orientation === v ? 'bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 shadow-xs' : 'text-slate-500'}`}>
                <Icon className="w-3.5 h-3.5" /> {l}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 min-w-[12rem]">
          <label className={labelCls} htmlFor="lm-root">Gyökérelem felirata</label>
          <input id="lm-root" className={inputCls} value={draft.root_title} maxLength={160} placeholder={tenantName || 'A szervezet'}
            onChange={(e) => setDraft({ ...draft, root_title: e.target.value })} />
        </div>
        {canEdit && (
          <button type="button" onClick={saveMap} disabled={!dirty || busy}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-40 cursor-pointer">
            <Save className="w-3.5 h-3.5" /> Beállítás mentése
          </button>
        )}
        {msg && <span className="text-xs text-emerald-700 dark:text-emerald-400" role="status">{msg}</span>}
      </div>

      {canEdit && (
        <div className="p-3 rounded-2xl border border-indigo-200 dark:border-indigo-800/50 bg-indigo-50/60 dark:bg-indigo-950/30 text-xs text-indigo-950 dark:text-indigo-200 flex items-start gap-2.5">
          <Info className="w-4 h-4 shrink-0 mt-0.5 text-indigo-600 dark:text-indigo-400" />
          <p>Húzd a szervet egy másikra → hozzá kapcsolódik; a gyökérre húzva közvetlenül az anyaszervezethez. A doboz fölött: sorrend, <b>Mellé / Alá</b>, <b>+</b> hozzá kapcsolódó szerv, ceruza: szerkesztés (ott állítható a kapcsolat típusa és a részesedés). Az átrendezés azonnal mentődik.</p>
        </div>
      )}
      {!mapReady && (
        <p className="p-3 rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/30 text-xs text-amber-900 dark:text-amber-200">
          A kapcsolati térkép az adatbázis frissítése (v61) után szerkeszthető: Adatbázis-séma &amp; Teljes mentés → Frissítés.
        </p>
      )}

      <div className={`rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-950/40 relative ${busy ? 'opacity-70' : ''}`}>
        {busy && <RefreshCw className="w-4 h-4 animate-spin text-indigo-500 absolute top-3 right-3" />}
        {entities.length ? (
          <LinkedMapView data={{ entities, settings: { relation_types: settings.relation_types, map: draft }, rootTitle: draft.root_title || tenantName }} edit={edit as never} />
        ) : (
          <p className="py-12 text-center text-sm text-slate-500">Még nincs kapcsolt szerv.</p>
        )}
      </div>

      <div className="p-3 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 space-y-2">
        <div className="flex items-center gap-2 text-xs font-bold text-slate-800 dark:text-slate-200"><Network className="w-4 h-4 text-indigo-600" /> Beszúrás a bejegyzésekbe (élő hivatkozás)</div>
        <p className="text-[11px] text-slate-500 dark:text-slate-400">
          A bejegyzés leírásánál a <b>Változó</b> gomb → <i>Kapcsolt szervek</i> csoportból választható, vagy másold be. Egy kapcsolt intézmény belső szervezete a Személyzet &amp; Pozíciók menüben, az intézmény kiválasztása után másolható.
        </p>
        <div className="flex flex-wrap gap-1.5">
          {tokens.map((x) => (
            <button key={x.t} type="button" onClick={() => copy(x.t)} title={`${x.t} másolása`}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 text-[11px] text-slate-700 dark:text-slate-300 hover:border-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 cursor-pointer">
              {copied === x.t ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3 text-slate-400" />} {x.l}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export { relationLabel };
